// /components/composer/ComposerProvider.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ComposerCtx } from './useComposer';
import Share from '../share/Share';

export default function ComposerProvider({ children }) {
  const [isOpen, setOpen] = useState(false);
  const [prefill, setPrefill] = useState({ text: '', media: '' });

  const openComposer = useCallback(({ text = '', media = '' } = {}) => {
    setPrefill({ text, media });
    setOpen(true);
  }, []);
  const close = useCallback(() => setOpen(false), []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = {
      overflow: document.body.style.overflow,
      paddingRight: document.body.style.paddingRight,
    };
    // prevent layout shift when scrollbar disappears
    const scrollbarW = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarW > 0) {
      document.body.style.paddingRight = `${scrollbarW}px`;
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.paddingRight = prev.paddingRight;
    };
  }, [isOpen]);

  const ctx = useMemo(() => ({ openComposer }), [openComposer]);

  return (
    <ComposerCtx.Provider value={ctx}>
      {children}
      {isOpen &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Create post"
            onClick={close}
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 9999,
              background: 'rgba(0,0,0,0.6)',
              // prevent any page scroll or overscroll bounce
              overflow: 'hidden',
              overscrollBehavior: 'contain',
              // center content
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              tabIndex={-1}
              style={{
                // box
                width: 'min(680px, 92vw)',
                maxHeight: 'calc(100vh - 24px)',
                background: 'var(--surface, #111)',
                color: 'inherit',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 16,
                boxShadow: '0 10px 40px rgba(0,0,0,0.45)',
                // CRITICAL: no overflow unless we truly exceed maxHeight
                overflow: 'hidden',
                // contain paints so outer box doesn’t create scrollbars from shadows
                contain: 'layout paint',
              }}
            >
              {/* inner padding wrapper prevents child margins from causing overflow */}
              <div
                style={{
                  padding: 16,
                  // neutralize first/last child margins to avoid phantom overflow
                  // (inline styles emulate what :where(*:first-child) resets would do)
                }}
              >
                <Share
                  initialDesc={prefill.text}
                  forcedImgUrl={prefill.media}
                  onSubmitSuccess={close}
                  setNewPost={() => {}}
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </ComposerCtx.Provider>
  );
}
